import { useTranslation } from 'react-i18next';
/**
 * QRPrintLayout - Print preview and print functionality for QR codes
 *
 * Provides:
 * - Grid layout for QR codes optimized for printing
 * - Print button that triggers browser print dialog
 * - Download as HTML functionality
 * - Printing recommendations for label sizes
 *
 * Print-optimized CSS:
 * - Hides non-printable elements
 * - Uses mm units for consistent physical sizing
 * - Includes page-break-inside:avoid for clean page breaks
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Printer, Download, Info, Loader2 } from 'lucide-react';
import { generatePrintHTML, type BinQRData, type QRBinType } from './qr-print-utils';
import { printHtmlViaIframe } from '@/lib/print-utils';

interface QRPrintLayoutProps {
  qrCodes: BinQRData[];
  binType: QRBinType;
  onClose?: () => void;
}

/**
 * Preview styles for the dialog (screen only)
 */
const previewStyles = `
  .qr-preview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 16px;
    padding: 8px;
  }

  .qr-preview-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fafafa;
  }

  .qr-preview-image {
    width: 100px;
    height: 100px;
  }

  .qr-preview-label {
    font-size: 14px;
    font-weight: 600;
    margin-top: 8px;
    font-family: system-ui, -apple-system, sans-serif;
  }

  .qr-preview-type {
    font-size: 10px;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-family: system-ui, -apple-system, sans-serif;
    margin-top: 4px;
  }
`;

export function QRPrintLayout({ qrCodes, binType, onClose }: QRPrintLayoutProps) {
  const { t } = useTranslation('settings');
  const getTypeLabel = () => {
    switch (binType) {
      case 'REGULAR':
        return 'BIN';
      case 'OOS':
        return 'OOS';
      case 'SHELF':
        return 'SHELF';
      case 'BENCH':
        return 'BENCH';
      case 'FILM_SLOT':
        return 'FILM STORAGE';
      default:
        return binType;
    }
  };

  const typeLabel = getTypeLabel();

  const handlePrint = () => {
    const printHTML = generatePrintHTML(qrCodes, binType, typeLabel);
    printHtmlViaIframe(printHTML);
  };

  const handleDownloadHTML = () => {
    const printHTML = generatePrintHTML(qrCodes, binType, typeLabel);
    const blob = new Blob([printHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${binType.toLowerCase()}-qr-codes.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={handlePrint} className="h-12 gap-2">
          <Printer className="h-5 w-5" aria-hidden="true" />
          {t('production.qrPrint.printQrCodes')}
        </Button>
        <Button
          variant="outline"
          onClick={handleDownloadHTML}
          className="h-12 gap-2"
        >
          <Download className="h-5 w-5" aria-hidden="true" />
          {t('production.qrPrint.downloadHtml')}
        </Button>
        {onClose && (
          <Button variant="ghost" onClick={onClose} className="h-12 ml-auto">
            {t('production.qrPrint.close')}
          </Button>
        )}
      </div>

      {/* Printing info */}
      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h4 className="font-medium mb-2">{t('production.qrPrint.printingInfo')}</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>{t('production.qrPrint.printingInfoItem1')}</li>
              <li>{t('production.qrPrint.printingInfoItem2')}</li>
              <li>{t('production.qrPrint.printingInfoItem3')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Preview Grid */}
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-3">
          {t('production.qrPrint.preview', { count: qrCodes.length, plural: qrCodes.length !== 1 ? 's' : '' })}
        </h4>
        <div
          className="qr-preview-grid"
          role="list"
          aria-label={`${binType} QR codes preview`}
        >
          {qrCodes.map((qr) => (
            <div key={qr.label} className="qr-preview-item" role="listitem">
              <img
                src={qr.qrDataUrl}
                alt={`QR code for ${qr.label}`}
                className="qr-preview-image"
              />
              <span className="qr-preview-label">{qr.label}</span>
              <span className="qr-preview-type">{typeLabel}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Preview styles */}
      <style>{previewStyles}</style>
    </div>
  );
}

/**
 * QRPreviewDialog - Dialog wrapper for QR print layout
 *
 * Provides a modal dialog that contains the QRPrintLayout component.
 * Used as a trigger for viewing and printing QR codes.
 */
interface QRPreviewDialogProps {
  children: React.ReactNode;
  qrCodes: BinQRData[];
  binType: QRBinType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** True while the QR sheet is still being fetched — shows a spinner instead
   *  of the (wrong-domain "create bins") empty state during the load. Callers
   *  with a lazy query gated on `open` should pass their `isFetching`. */
  loading?: boolean;
}

export function QRPreviewDialog({
  children,
  qrCodes,
  binType,
  open,
  onOpenChange,
  loading = false,
}: QRPreviewDialogProps) {
  const { t } = useTranslation('settings');
  const title =
    binType === 'FILM_SLOT'
      ? t('production.qrPrint.filmStorageQrCodes')
      : binType === 'SHELF'
        ? t('production.qrPrint.packingShelfQr')
        : binType === 'OOS'
          ? t('production.qrPrint.oosBinQrCodes')
          : binType === 'BENCH'
            ? t('production.qrPrint.benchQrCodes')
            : t('production.qrPrint.regularBinQrCodes');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {qrCodes.length > 0 ? (
          <QRPrintLayout qrCodes={qrCodes} binType={binType} />
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" role="status" aria-busy="true">
            <Loader2 className="h-6 w-6 animate-spin mb-2" aria-hidden="true" />
            <p>{t('production.qrPrint.generating')}</p>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p>{t('production.qrPrint.noQrCodes')}</p>
            <p className="text-sm mt-2">{t('production.qrPrint.createBinsFirst')}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
